'use client';

import React, { useEffect, useMemo, useState } from 'react';

import { FundingKey } from '@bytel/leviator';
import {
    Box,
    BoxContent,
    Button,
    ButtonMarkup,
    ButtonVariant,
    Divider,
    GapSize,
    Icon,
    IconName,
    IconSize,
    Link,
    RadioTiles,
    Rows,
    Spacer,
    SpacerSize,
    Sticker,
    Text,
    TextLevels,
    TextMarkup,
    TrilogyColor,
    TypographyBold,
    VariantState,
    View,
} from '@bytel/trilogy-react';

import { getDefaultFunding, getDefaultPlan } from '@services/products';

import { WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { MobilePlanType, RenewDelayType } from '@app-types/plan';
import { UserType, UserTypeEnum } from '@app-types/user';

import { PlanDetailsBox, PlanRenewDelayAlert } from '@components/plan';

import { MarketingsState, PartialMarketingsState } from '@hooks/use-marketings';

import { paths } from '@helpers/path';
import { formatPrice } from '@helpers/wording';

import { PlanRange, PlanSubTypeId, PlanTypeId } from '@constants/plan';

import {
    CategoryBox,
    EasyTradeInBlock,
    PromoHome,
    PromoPortability,
    SelectFunding,
    SkeletonPlan,
    SubTypeSelector,
    WithOrWithoutPlan,
} from '@product-detail/components';
import { PricesDetails, revalidateH2Counter, sendSmartChangeTracking } from '@product-detail/helpers';
import { UseEasyTradeInReturn } from '@product-detail/hooks';

import { PlanRadioTile } from './plan-radio-tile';

const findPlanByPriority = (
    plans: MobilePlanType[],
    priorities: Array<(plan: MobilePlanType) => boolean>,
): MobilePlanType | null => {
    for (const condition of priorities) {
        const found = plans.find(condition);
        if (found) return found;
    }
    return null;
};

const renewDelayNumber = (renewDelay?: RenewDelayType): number | undefined => {
    switch (renewDelay) {
        case RenewDelayType.MONTHLY_12:
            return 12;
        case RenewDelayType.MONTHLY_24:
            return 24;
        default:
            return undefined;
    }
};

interface PlansProps {
    type: WallType;
    plans: MobilePlanType[];
    currentPlan: MobilePlanType | null;
    setCurrentPlan: (plan: MobilePlanType | null) => void;
    marketings: MarketingsState;
    setMarketings: (marketings: PartialMarketingsState) => MarketingsState;
    openPlanModal: () => void;
    setFunding: (key: FundingKey) => void;
    planSubType: PlanSubTypeId.CLASSIC | PlanSubTypeId.SMART_CHANGE;
    prices: PricesDetails;
    isLoading: boolean;
    user?: UserType;
    isRenewal: boolean;
    contract?: ContractType;
    fundingKey?: FundingKey;
    withoutPlanPrice: number;
    handleOpenSmartChangeModal: () => void;
    simulatedPortabilitySelected: boolean;
    onUpdatePortability: (isSelected: boolean) => void;
    hasEasyTradeIn: boolean;
    easyTradeIn: UseEasyTradeInReturn;
    tradeInBonus: number | undefined;
    easyTradeInEnabled: boolean;
    hasTradeIn: boolean;
    resetTradeIn: () => void;
}

export function Plans({
    type,
    plans,
    currentPlan,
    setCurrentPlan,
    marketings,
    setMarketings,
    openPlanModal,
    setFunding,
    planSubType,
    prices,
    isLoading,
    user,
    isRenewal,
    contract,
    fundingKey,
    withoutPlanPrice,
    handleOpenSmartChangeModal,
    simulatedPortabilitySelected,
    onUpdatePortability,
    hasEasyTradeIn,
    easyTradeIn,
    tradeInBonus,
    easyTradeInEnabled,
    hasTradeIn,
    resetTradeIn,
}: Readonly<PlansProps>) {
    const [previousPlans, setPreviousPlans] = useState<{
        [PlanSubTypeId.CLASSIC]: string | null;
        [PlanSubTypeId.SMART_CHANGE]: string | null;
    }>({
        [PlanSubTypeId.CLASSIC]: null,
        [PlanSubTypeId.SMART_CHANGE]: null,
    });

    const selectPlanForSubType = (selectedSubType: PlanSubTypeId.CLASSIC | PlanSubTypeId.SMART_CHANGE) => {
        const defaultPlanGencode = getDefaultPlan(
            isRenewal,
            plans.map((p) => ({ subType: p.subTypeId, gencode: p.gencode })),
            selectedSubType === PlanSubTypeId.SMART_CHANGE,
        ).toString();

        const priorities = [
            (p: MobilePlanType) => p.gencode === previousPlans[selectedSubType],
            (p: MobilePlanType) => p.gencode === defaultPlanGencode,
            (p: MobilePlanType) => p.subTypeId === selectedSubType,
        ];

        return findPlanByPriority(plans, priorities);
    };

    const handleSubTypeChange = (selectedSubType: PlanSubTypeId.CLASSIC | PlanSubTypeId.SMART_CHANGE) => {
        sendSmartChangeTracking(isRenewal, selectedSubType === PlanSubTypeId.SMART_CHANGE);
        const newPlan = selectPlanForSubType(selectedSubType);
        setCurrentPlan(newPlan);
        const defaultFunding = getDefaultFunding(newPlan?.fundings ?? []);
        if (defaultFunding) {
            setFunding(defaultFunding.key);
        }
    };

    const handleWithOrWithoutPlan = (hasPlan: boolean) => {
        if (hasPlan) {
            setCurrentPlan(selectPlanForSubType(PlanSubTypeId.CLASSIC));
        } else {
            setCurrentPlan(null);
        }
        revalidateH2Counter();
    };

    const ownedPlan = useMemo(() => plans.find((p) => p.isOwned), [plans]);

    const isOwnedPlanDisabled = useMemo(() => {
        if (!isRenewal || user?.user_type !== UserTypeEnum.CDC || !ownedPlan) {
            return false;
        }
        if (ownedPlan.subTypeId === PlanSubTypeId.PARTNER) {
            return true;
        }
        return ownedPlan.typeId.includes(PlanTypeId.SOWO) || ownedPlan.commercialRange === PlanRange.WITHOUT_MOBILE;
    }, [isRenewal, user, ownedPlan]);

    const shouldDisplayRenewDelayAlert = useMemo(() => {
        if (!isRenewal || !ownedPlan || !currentPlan) {
            return false;
        }

        const ownedRenewDelay = renewDelayNumber(ownedPlan.renewDelay);
        const currentRenewDelay = renewDelayNumber(currentPlan.renewDelay);

        if (ownedRenewDelay === undefined || currentRenewDelay === undefined) {
            return false;
        }

        return ownedRenewDelay !== currentRenewDelay;
    }, [isRenewal, ownedPlan, currentPlan]);

    useEffect(() => {
        if (!isOwnedPlanDisabled || !ownedPlan || currentPlan?.gencode !== ownedPlan.gencode) {
            return;
        }

        const otherPlan = plans.find((p) => p.gencode !== ownedPlan.gencode);
        if (otherPlan) {
            setCurrentPlan(otherPlan);
        } else {
            setCurrentPlan(null);
        }
    }, [isOwnedPlanDisabled, ownedPlan, currentPlan, plans, setCurrentPlan]);

    useEffect(() => {
        if (currentPlan) {
            setPreviousPlans((prev) => ({
                ...prev,
                [currentPlan.subTypeId]: currentPlan.gencode,
            }));
        }
    }, [currentPlan]);

    if (plans.length === 0) return null;

    if (isLoading) return <SkeletonPlan type={type} isRenewal={isRenewal} />;

    return (
        <>
            {currentPlan !== undefined && (
                <>
                    {!isRenewal && (
                        <WithOrWithoutPlan
                            type={type}
                            plans={plans}
                            currentPlan={currentPlan}
                            setCurrentPlan={handleWithOrWithoutPlan}
                            withoutPlanPrice={withoutPlanPrice}
                        />
                    )}
                    {currentPlan !== null && (
                        <SubTypeSelector
                            planSubType={planSubType}
                            setPlanSubType={handleSubTypeChange}
                            plans={plans}
                            handleOpenSmartChangeModal={handleOpenSmartChangeModal}
                        />
                    )}
                </>
            )}
            {currentPlan !== null && (
                <CategoryBox title='Quel forfait vous faut-il&nbsp;?' formId='offerSelect'>
                    {isRenewal && !ownedPlan && planSubType === PlanSubTypeId.CLASSIC && (
                        <>
                            <Text level={TextLevels.TWO}>
                                Votre forfait actuel n&apos;est pas proposé en renouvellement. Découvrez nos autres
                                offres, pensées pour répondre à vos besoins.
                            </Text>
                            <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                Choisir un nouveau forfait
                            </Text>
                            <Spacer size={SpacerSize.THREE} />
                        </>
                    )}
                    {isRenewal && ownedPlan && ownedPlan.subTypeId === planSubType && (
                        <div className='plan-radio-list'>
                            <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                Conserver mon forfait actuel
                            </Text>
                            <PlanRadioTile
                                plan={ownedPlan}
                                currentPlan={currentPlan}
                                onPlanChange={(plan) => {
                                    setCurrentPlan(plan);
                                }}
                                disabled={isOwnedPlanDisabled}
                            />
                            <Divider content='Ou' />
                            <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                Changer de forfait
                            </Text>
                            <Spacer size={SpacerSize.THREE} />
                        </div>
                    )}
                    <RadioTiles
                        className={planSubType === PlanSubTypeId.SMART_CHANGE ? 'plan-radio-list' : 'grid-radio-list'}
                    >
                        {plans
                            .filter((p) => p.gencode !== ownedPlan?.gencode && p.subTypeId === planSubType)
                            .map((plan) => (
                                <PlanRadioTile
                                    key={plan.gencode}
                                    plan={plan}
                                    currentPlan={currentPlan}
                                    onPlanChange={setCurrentPlan}
                                />
                            ))}
                    </RadioTiles>
                    {isRenewal && plans.length <= 1 && (
                        <>
                            <Box shadowless flat backgroundColor={TrilogyColor.INFO_FADE}>
                                <BoxContent>
                                    <View flexable className='gap-medium'>
                                        <Icon name={IconName.USER_HEADSET} size={IconSize.LARGE} />
                                        <div>
                                            <Text level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                                Appelez gratuitement le 1064 pour changer de forfait
                                            </Text>
                                            <Text level={TextLevels.TWO}>
                                                Nos conseillers vous accompagnent pour trouver le forfait qui vous va
                                                bien.
                                            </Text>
                                        </div>
                                    </View>
                                    <Spacer size={SpacerSize.TWO} />
                                    <Text level={TextLevels.THREE}>
                                        <Text markup={TextMarkup.SPAN}>Service clients ouvert</Text>
                                        <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                            {' '}
                                            du lundi au samedi de 8h à 20h.
                                        </Text>
                                    </Text>
                                    <Text level={TextLevels.TWO}>
                                        Ou rendez-vous sur votre{' '}
                                        <Link
                                            href={contract?.id ? paths.migrationOffer(contract.id) : paths.clientSpace}
                                        >
                                            parcours de migration d&apos;offre.
                                        </Link>
                                    </Text>
                                </BoxContent>
                            </Box>
                            <Button
                                markup={ButtonMarkup.A}
                                variant={ButtonVariant.PRIMARY}
                                href='tel:1064'
                                className='is-hidden-tablet'
                                fullwidth
                            >
                                Appeler le 1064
                            </Button>
                        </>
                    )}
                    {isRenewal && shouldDisplayRenewDelayAlert && (
                        <PlanRenewDelayAlert renewDelay={renewDelayNumber(ownedPlan?.renewDelay)} />
                    )}
                    {currentPlan && (
                        <PlanDetailsBox
                            plan={currentPlan}
                            prices={prices.planPrices}
                            onOpenModal={openPlanModal}
                            isRenewal={isRenewal}
                        >
                            {currentPlan.renewalDiscounts?.map((discount) => {
                                if (!discount.recurringPrice?.priceIncludingTax) return null;
                                return (
                                    <Sticker
                                        key={discount.offerId}
                                        small
                                        variant={VariantState.ACCENT}
                                        label={`Remise client\u00A0: ${
                                            discount.recurringPrice?.priceIncludingTax &&
                                            Math.abs(discount.recurringPrice.priceIncludingTax) >= 0
                                                ? formatPrice(discount.recurringPrice.priceIncludingTax)
                                                : null
                                        }`}
                                    />
                                );
                            })}
                        </PlanDetailsBox>
                    )}
                    {currentPlan && !isRenewal && (
                        <Rows gap={GapSize.FIVE}>
                            <PromoHome
                                currentPlan={currentPlan}
                                setMarketings={setMarketings}
                                marketings={marketings}
                                user={user}
                                isRenewal={isRenewal}
                            />
                            <PromoPortability
                                currentPlan={currentPlan}
                                isRenewal={isRenewal}
                                onUpdatePortability={onUpdatePortability}
                                simulatedPortabilitySelected={simulatedPortabilitySelected}
                            />
                        </Rows>
                    )}
                </CategoryBox>
            )}
            {type === WallType.PHONE && hasEasyTradeIn && (
                <EasyTradeInBlock
                    choice={easyTradeIn.choice}
                    setChoice={easyTradeIn.setChoice}
                    confirmedResult={easyTradeIn.confirmedResult}
                    onOpenModal={easyTradeIn.openModal}
                    showReturnHint={easyTradeIn.hasReturnHint}
                    onReset={easyTradeIn.reset}
                    tradeInBonus={tradeInBonus}
                    easyTradeInEnabled={easyTradeInEnabled}
                    hasTradeIn={hasTradeIn}
                    resetTradeIn={resetTradeIn}
                />
            )}
            {currentPlan !== undefined && (
                <CategoryBox
                    title={
                        new Set(currentPlan?.fundings?.map((f) => f.key)).size > 1
                            ? 'Quel paiement pour votre téléphone\u00A0?'
                            : 'Votre paiement en détail'
                    }
                    formId='fundingSelect'
                >
                    <SelectFunding
                        currentPlan={currentPlan}
                        fundingKey={fundingKey}
                        onFundingKeyChange={setFunding}
                        isRenewal={isRenewal}
                        prices={prices}
                        withoutPlanPrice={withoutPlanPrice}
                    />
                </CategoryBox>
            )}
        </>
    );
}
